// aave v2 style
import { ProtocolDataProviderAbi } from '../../abis/aave-v2/ProtocolDataProvider'
import { IncentivesControllerAbi } from '../../abis/lendle/IncentivesController'
import { RewarderAbi } from '../../abis/aurelius/Rewarder'
import { UniswapV2PairAbi } from '../../abis/oracle/UniswapV2Pair'

// aave v3 style
import { AavePoolV3UpgradedAbi } from '../../abis/aave/AavePoolV3Upgraded'
import { AaveV3ProtocolDataProviderAbi } from '../../abis/aave/AaveV3ProtocolDataProvider'

// init
import { InitLensAbi } from '../../abis/init/InitLens'

// compound v3
import { CometAbi } from '../../abis/compound-v3/Comet'
import { CometExtAbi } from '../../abis/compound-v3/CometExt'
import { IrgetterAbi } from '../../abis/compound-v3/IrGetter'

import {
  isAaveV2Type,
  isAaveV32Type,
  isAaveV3Type,
  isCompoundV2Type,
  isCompoundV3,
  isInit,
  isEulerType,
  isFluid,
  isGearboxV3,
  isMorphoType,
  isMultiMarket,
  isSumerType,
  isDolomite,
} from '../../utils'
import { AdditionalYields, MulticallRetryFunction } from '../../types'
import { buildAaveV2StyleLenderReserveCall } from './aave-v2-type/publicCallBuild'
import { buildAaveV3StyleLenderReserveCall } from './aave-v3-type/publicCallBuild'
import { buildInitStyleLenderReserveCall } from './init/publicCallBuild'
import { buildCompoundV3StyleLenderReserveCall } from './compound-v3/publicCallBuild'
import { getAaveV2ReservesDataConverter } from './aave-v2-type/publicCallParse'
import { getAaveV3ReservesDataConverter } from './aave-v3-type/publicCallParse'
import { getInitReservesDataConverter } from './init/publicCallParse'
import { getCompoundV3ReservesDataConverter } from './compound-v3/publicCallParse'
import { AavePoolLegacyAbi } from '../../abis/aave-v3/AavePoolLegacy'
import { AavePoolDataProviderLegacyAbi } from '../../abis/aave-v3/AavePoolDataProviderLegacy'
import { Lender } from '@1delta/lender-registry'
import { Call } from 'viem'
import { YLDRProtocolDataProviderAbi } from '../../abis/aave-v2/YLDR'
import { buildMorphoTypeCall } from './morpho/publicCallBuild'
import { getMorphoMarketDataConverter } from './morpho/getMarketsFromChain'
import { getListaMarketDataConverter } from './lista/getMarketsFromChain'
import { fetchListaFixedTerms } from './lista/listaBroker'
import { morphoTypeMarkets } from '@1delta/data-sdk'
import { MorphoLensAbi } from '@1delta/abis'
import { GenericTokenList } from '../../types/general'
import { buildCompoundV2StyleLenderReserveCall } from './compound-v2/publicCallBuild'
import { resolveRateUnitSeconds } from './compound-v2/rateClock'
import { getCompoundV2DataConverter } from './compound-v2/publicCallParse'
import { VenusLensAbi } from '../../abis/compound-v2/VenusLens'
import {
  SumerLensAbi,
  SumerComptrollerAbi,
} from '../../abis/compound-v2/SumerLens'
import { TakaraMarketStateAbi } from '../../abis/compound-v2/TakaraMarketState'

// euler v2
import { vaultLensAbi } from '../../abis/euler/vaultLens'
import { buildEulerV2LenderReserveCall } from './euler/publicCallBuild'
import { getEulerV2ReservesDataConverter } from './euler/publicCallParse'

// aave v4
import { AaveV4SpokeAbi, AaveV4HubAbi, AaveV4OracleAbi } from '@1delta/abis'
import { buildAaveV4LenderReserveCall } from './aave-v4-type/publicCallBuild'
import { getAaveV4ReservesDataConverter } from './aave-v4-type/publicCallParse'
import {
  isAaveV4Type,
  isSiloV2Type,
  isSiloV3Type,
} from '@1delta/lender-registry'

// silo v2
import { SiloAbi, SiloLensAbi } from '../../abis/silo-v2'
import { InterestRateModelV2Abi } from '../../abis/silo-v2/InterestRateModelV2'
import { buildSiloV2LenderReserveCall } from './silo-v2/publicCallBuild'
import { getSiloV2ReservesDataConverter } from './silo-v2/publicCallParse'

// silo v3 — reuses the v2 ABIs (on-chain interface is identical)
import { buildSiloV3LenderReserveCall } from './silo-v3/publicCallBuild'
import { getSiloV3ReservesDataConverter } from './silo-v3/publicCallParse'

// fluid
import { buildFluidCall } from './fluid/publicCallBuild'
import { getFluidPublicDataConverter } from './fluid/publicCallParse'
import { fetchFluidApiData } from './fluid/apiData'
import { fetchFluidDexState } from './fluid/dexState'
import { buildDolomiteCall } from './dolomite/publicCallBuild'
import { getDolomitePublicDataConverter } from './dolomite/publicCallParse'
import { FluidLiquidityResolverAbi, FluidVaultResolverAbi } from '@1delta/abis'
import { DolomiteMarginAbi } from '@1delta/abis'

// gearbox v3.1 (market compressor)
import { buildGearboxCall } from './gearbox/publicCallBuild'
import { getGearboxV3PublicDataConverter } from './gearbox/publicCallParse'
import { GearboxMarketCompressorV310Abi } from '@1delta/abis'

export function getMorphoTypeMarketConverter(
  lender: string,
  chainId: string,
  prices: { [a: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
  marketsOverride?: string[],
) {
  if (lender.startsWith('LISTA_DAO'))
    return getListaMarketDataConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      tokenList,
      marketsOverride,
    )
  return getMorphoMarketDataConverter(
    lender,
    chainId,
    prices,
    additionalYields,
    tokenList,
    marketsOverride,
  )
}

export function buildLenderCall(chainId: string, lender: string) {
  if (isAaveV2Type(lender))
    return buildAaveV2StyleLenderReserveCall(chainId, lender)
  if (isAaveV3Type(lender))
    return buildAaveV3StyleLenderReserveCall(chainId, lender)
  if (isCompoundV3(lender))
    return buildCompoundV3StyleLenderReserveCall(chainId, lender)
  if (isInit(lender)) return buildInitStyleLenderReserveCall(chainId, lender)
  if (isMorphoType(lender)) {
    return buildMorphoTypeCall(chainId, lender)
  }
  if (isCompoundV2Type(lender))
    return buildCompoundV2StyleLenderReserveCall(chainId, lender)
  if (isEulerType(lender)) return buildEulerV2LenderReserveCall(chainId, lender)
  if (isAaveV4Type(lender)) return buildAaveV4LenderReserveCall(chainId, lender)
  if (isSiloV2Type(lender)) return buildSiloV2LenderReserveCall(chainId, lender)
  if (isSiloV3Type(lender)) return buildSiloV3LenderReserveCall(chainId, lender)
  // Fluid: single-entry fetch — the two-call payload emits FLUID_LENDING
  // plus every FLUID_VAULT_<id> bucket. FLUID_VAULT_* keys are synthesized
  // by the converter and never dispatched directly.
  if (isFluid(lender)) return buildFluidCall(chainId, lender)
  if (isDolomite(lender)) return buildDolomiteCall(chainId, lender)
  // Gearbox V3: single-entry fetch — DataCompressorV3 returns every pool
  // on the chain (with its embedded CreditManager list) in one call.
  // Per-CM `GEARBOX_V3_<CM_HEX>` keys are synthesized by the converter.
  if (isGearboxV3(lender)) return buildGearboxCall(chainId, lender)
  return []
}

export function getLenderDataConverter(
  lender: string,
  chainId: string,
  prices: { [a: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
  /** Compound V2 only — see `compound-v2/rateClock.ts`. */
  unitSeconds?: number,
) {
  if (isAaveV2Type(lender))
    return getAaveV2ReservesDataConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      tokenList,
    )
  if (isAaveV3Type(lender))
    return getAaveV3ReservesDataConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      tokenList,
    )
  if (isCompoundV3(lender))
    return getCompoundV3ReservesDataConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      tokenList,
    )

  if (isInit(lender))
    return getInitReservesDataConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      tokenList,
    )

  if (isMorphoType(lender)) {
    return getMorphoTypeMarketConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      tokenList,
    )
  }

  if (isCompoundV2Type(lender))
    return getCompoundV2DataConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      tokenList,
      unitSeconds,
    )
  if (isEulerType(lender))
    return getEulerV2ReservesDataConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      tokenList,
    )
  if (isAaveV4Type(lender))
    return getAaveV4ReservesDataConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      tokenList,
    )
  if (isSiloV2Type(lender))
    return getSiloV2ReservesDataConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      tokenList,
    )
  if (isSiloV3Type(lender))
    return getSiloV3ReservesDataConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      tokenList,
    )
  if (isFluid(lender))
    return getFluidPublicDataConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      tokenList,
    )
  if (isGearboxV3(lender))
    return getGearboxV3PublicDataConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      tokenList,
    )
  if (isDolomite(lender))
    return getDolomitePublicDataConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      tokenList,
    )
  return [() => null, 0] as any
}

const getAbi = (lender: string) => {
  if (isAaveV2Type(lender))
    return [
      ...ProtocolDataProviderAbi,
      ...UniswapV2PairAbi,
      ...RewarderAbi,
      ...IncentivesControllerAbi,
    ]

  if (isAaveV32Type(lender))
    return [...AavePoolV3UpgradedAbi, ...AaveV3ProtocolDataProviderAbi]
  if (lender === Lender.YLDR) return [...YLDRProtocolDataProviderAbi]

  if (isAaveV3Type(lender))
    return [...AavePoolLegacyAbi, ...AavePoolDataProviderLegacyAbi]

  if (isCompoundV3(lender)) return [...IrgetterAbi, ...CometAbi, ...CometExtAbi]

  if (isInit(lender)) return InitLensAbi

  if (isMorphoType(lender)) return MorphoLensAbi
  if (isEulerType(lender)) return vaultLensAbi as any
  if (isAaveV4Type(lender))
    return [...AaveV4SpokeAbi, ...AaveV4OracleAbi, ...AaveV4HubAbi]
  if (isSiloV2Type(lender))
    return [...SiloAbi, ...SiloLensAbi, ...InterestRateModelV2Abi]
  if (isSiloV3Type(lender))
    return [...SiloAbi, ...SiloLensAbi, ...InterestRateModelV2Abi]
  if (isFluid(lender))
    return [...FluidVaultResolverAbi, ...FluidLiquidityResolverAbi] as any
  if (isDolomite(lender)) return [...DolomiteMarginAbi] as any
  if (isGearboxV3(lender)) return [...GearboxMarketCompressorV310Abi] as any
  if (isSumerType(lender)) return [...SumerLensAbi, ...SumerComptrollerAbi]
  if (lender === Lender.TAKARA) return [...TakaraMarketStateAbi]
  if (isCompoundV2Type(lender)) return VenusLensAbi

  return []
}

export const getLenderPublicData = async (
  chainId: string,
  lenders: string[],
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  multicallRetry: MulticallRetryFunction,
  tokenList: () => Promise<GenericTokenList> = async () => {
    return {}
  },
): Promise<{ [lender: string]: any }> => {
  let calls: {
    call: Call
    abi: any
  }[] = []

  // Drop lenders whose builder produces calls with an undefined address —
  // typically a Compound V2 fork (or similar) that has tokens registered
  // for the chain but no lens contract configured. Without this guard a
  // single misconfigured lender throws inside viem's multicall and takes
  // down every other lender's data on the chain.
  const validLenders: string[] = []
  // Start offset of each lender's slice inside `calls`, so a lender that needs
  // a second, data-dependent fetch (Fluid: DEX state keyed on the vault list)
  // can read its own raw results before the conversion loop runs.
  const sliceStart: { [lender: string]: number } = {}
  // Number of calls each lender ACTUALLY put into `calls`. This — not the
  // converter's declared length — is what its result slice is cut by; see
  // `sliceLenderResults`.
  const sliceBuilt: { [lender: string]: number } = {}
  for (const lender of lenders) {
    const abi = getAbi(lender)
    const callData = buildLenderCall(chainId, lender)
    // @ts-ignore
    if (callData.some((c: any) => !c.address)) continue
    // @ts-ignore
    const mappedCalls = callData.map((call) => ({ call, abi: call.abi ?? abi }))
    sliceStart[lender] = calls.length
    sliceBuilt[lender] = mappedCalls.length
    // @ts-ignore
    calls.push(...mappedCalls)
    validLenders.push(lender)
  }
  /** fetch list and on-chainData */
  const [rawResults, list] = await Promise.all([
    multicallRetry({
      chain: chainId,
      calls: calls.map((call) => call.call) as any[],
      abi: calls.map((call) => call.abi),
      // No per-chain byte override any more. Ethereum used to pass 500 bytes
      // to keep one huge `aggregate3` from timing out — a workaround for the
      // 1024-byte default fan-out that `@1delta/providers` replaced with
      // count-based chunking (250 calls, two in flight). A 500-byte chunk is
      // now 4–14 calls issued nearly serially, i.e. slower than the default.
    }),
    tokenList(),
    // Fluid SMART vaults (T2/T3/T4) need the DEX trading yield, which is
    // published nowhere on-chain. Cached + single-flighted; primed here
    // because the converters below are synchronous. If it fails, a smart row
    // still serves correct amounts and its liquidity-layer rate legs, just
    // without the trading leg. T1 is unaffected either way.
    validLenders.some(isFluid)
      ? fetchFluidApiData(chainId).catch(() => undefined)
      : Promise.resolve(undefined),
    // Lista broker term menus (`getFixedTerms`) live behind a second hop
    // (`Moolah.brokers(id)`) and are read into a module cache the synchronous
    // converter consumes. Primed HERE, through the same `multicallRetry` as the
    // market read, because the converter's own fire-and-forget warm can only
    // serve the NEXT call — and on a box whose default RPC rejected the read it
    // served none: every brokered market ingested as a plain variable-rate
    // Moolah market with `variableBorrowDisabled: false` and no `terms`, while
    // the same code on a laptop was fine from the second pass. Failure leaves
    // the previous menu (or none) in place; the market rows are unaffected.
    validLenders.some((l) => l.startsWith('LISTA_DAO'))
      ? fetchListaFixedTerms(
          chainId,
          morphoTypeMarkets()?.LISTA_DAO?.[chainId] ?? [],
          multicallRetry,
        ).catch(() => undefined)
      : Promise.resolve(undefined),
  ])

  // Fluid SMART vaults hold collateral and/or debt as DEX LP SHARES, so their
  // totals mean nothing without the share->token conversion. It has to be
  // primed AFTER the multicall because pool discovery is a union of the DEX
  // resolver's enumeration and the pools the vaults actually reference (an
  // older DEX generation is not enumerable — see `fetchFluidDexState`). A
  // failure here leaves smart vaults skipped rather than mis-sized.
  const fluidLender = validLenders.find(isFluid)
  if (fluidLender !== undefined) {
    try {
      const start = sliceStart[fluidLender] ?? 0
      const vaults = (rawResults as any[])?.[start] as any[] | undefined
      const referenced = new Set<string>()
      for (const v of vaults ?? []) {
        if (!v?.isSmartCol && !v?.isSmartDebt) continue
        if (v?.isSmartCol && v?.constantVariables?.supply)
          referenced.add(String(v.constantVariables.supply).toLowerCase())
        if (v?.isSmartDebt && v?.constantVariables?.borrow)
          referenced.add(String(v.constantVariables.borrow).toLowerCase())
      }
      await fetchFluidDexState(chainId, multicallRetry, [...referenced])
    } catch {
      // smart vaults degrade to skipped; T1 keeps working
    }
  }

  const invalidLenders: string[] = []
  let lenderData: { [lender: string]: any } = {}

  // A Compound V2 fork's `*RatePerBlock` is denominated in whatever clock its
  // `accrueInterest` counts — seconds, chain blocks, or (on Arbitrum) L1
  // blocks — and forks on ONE chain disagree. Resolve it per fork from the
  // chain instead of annualizing off a per-chain table that cannot express
  // that and drifts as chains speed up. Failures resolve to `undefined`, which
  // keeps the legacy table. Cached for a day, so this is one call per fork per
  // day, not per fetch. See `compound-v2/rateClock.ts`.
  const rateUnitSeconds = new Map<string, number | undefined>()
  await Promise.all(
    validLenders
      .filter((l) => isCompoundV2Type(l))
      .map(async (l) => {
        try {
          rateUnitSeconds.set(l, await resolveRateUnitSeconds(chainId, l))
        } catch {
          rateUnitSeconds.set(l, undefined)
        }
      }),
  )

  const slices = sliceLenderResults(
    chainId,
    rawResults,
    validLenders.map((lender) => ({
      lender,
      start: sliceStart[lender],
      built: sliceBuilt[lender],
      declared: getLenderDataConverter(
        lender,
        chainId,
        prices,
        additionalYields,
        list,
        rateUnitSeconds.get(lender),
      )[1],
    })),
  )

  for (const lender of validLenders) {
    const data = slices[lender]
    // Mismatched or empty — nothing to convert. (Mismatch is already logged.)
    if (!data || data.length === 0) continue
    const [converter] = getLenderDataConverter(
      lender,
      chainId,
      prices,
      additionalYields,
      list,
      rateUnitSeconds.get(lender),
    )
    try {
      const convertedData = converter(data)
      if (isMultiMarket(lender)) {
        if (!convertedData) {
          invalidLenders.push(lender)
        } else {
          Object.assign(lenderData, convertedData)
        }
      } else {
        if (!convertedData) {
          invalidLenders.push(lender)
        } else {
          lenderData[lender] = convertedData
        }
      }
    } catch {
      // silently skip — failures are expected when markets are missing
    }
  }

  return lenderData
}

/**
 * Cut one lender's slice out of the batched multicall result.
 *
 * Every lender's slice is addressed by the offset and length recorded when
 * ITS calls were pushed — never by a cursor that runs across lenders. The old
 * loop advanced a cursor by each converter's DECLARED length, so one lender
 * whose parser counted more calls than its builder emitted (Dolomite on a
 * chain with zero markets: builder `[]`, parser `2`) shifted every lender
 * after it by that many slots, silently. On BNB that put each Euler vault's
 * lens result — name, asset, rates, flags, LTV config — under the marketUid
 * of the vault two places earlier in the registry, for six days, with every
 * deploy green and every row freshly timestamped.
 *
 * A lender whose declared length disagrees with what it built is dropped
 * (returned as `null`) and logged, instead of being handed a slice it will
 * misread. Its own data is lost for the cycle — which is what a parser/builder
 * disagreement deserves — but no other lender's is touched.
 *
 * Exported for tests.
 */
export function sliceLenderResults(
  chainId: string,
  rawResults: any[],
  entries: Array<{
    lender: string
    start: number
    built: number
    declared: number
  }>,
): { [lender: string]: any[] | null } {
  const out: { [lender: string]: any[] | null } = {}
  for (const { lender, start, built, declared } of entries) {
    if (declared !== built) {
      console.warn(
        `[lending] chain ${chainId} ${lender}: builder emitted ${built} calls but parser expects ${declared} — dropping this lender for the cycle (builder/parser out of sync)`,
      )
      out[lender] = null
      continue
    }
    out[lender] = built > 0 ? rawResults.slice(start, start + built) : []
  }
  return out
}
